import type { TFunction } from "i18next";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useAnalytics } from "../../contexts/analytics/hooks";
import { FEATURE_FLAGS } from "../../contexts/FeatureFlagsProvider";
import { useHospitalData } from "../../contexts/HospitalDataProvider";
import { useSnackbar } from "../../contexts/SnackbarContext";
import { useFormatPersonName } from "../../hooks/useNameFormat";
import { useFeatureFlag } from "../../hooks/useOpenFeatureFlag";
import type { Job, Nurse, NurseInShift } from "../../utils/models";
import { isSameShiftWindow, type ShiftWindow } from "../../utils/scheduleConflict";
import { shiftLocationLabel } from "../../utils/scheduleConflictCopy";
import { useUpdateLocationAndHoursForShift } from "../NormalizedNurseMenu/useUpdateLocationAndHours";
import { QUICK_ADD_ANALYTICS_TIME_FORMAT } from "./quickAddConsts";

type Params = {
	nurse: Nurse | null;
	/** The destination; `null` when none is picked, which the dialog never lets reach a save. */
	location: string | null;
	window: ShiftWindow;
	/** The job shown in the picker, and whether the user picked it themselves. */
	job: Job | undefined;
	jobTouched: boolean;
	/** The group header the dialog was opened from, reported alongside the move like an add reports it. */
	section: { name: string; locationKey: string };
	onClose: () => void;
};

/**
 * The job the moved shift goes onto: the user's pick, or `null` to keep the shift's own. Role mode has no job
 * picker, and an untouched picker only shows the shift's job (or none, when it is one this person no longer
 * holds), so in both the shift keeps its job.
 */
export const jobForMove = ({
	jobManagementEnabled,
	jobTouched,
	job,
}: {
	jobManagementEnabled: boolean;
	jobTouched: boolean;
	job: Job | undefined;
}): Job | null =>
	// DEFAULT: a picker the user cleared (no job resolved) keeps the shift's job too — a shift cannot be left
	// job-less from here, so keeping it is the only change-free reading of an empty pick.
	jobManagementEnabled && jobTouched && job !== undefined ? job : null;

/** The existing shift with the move applied: location, and the times / job only when they changed. */
const movedShift = (existing: NurseInShift, toLocation: string, window: ShiftWindow, newJob: Job | null, timesChanged: boolean): NurseInShift => ({
	...existing,
	location: toLocation,
	// Unchanged times keep the original objects: the update compares them by reference to decide whether the hours
	// changed, and an equal-but-new DateTime would rewrite them for nothing.
	startTime: timesChanged ? window.startTime : existing.startTime,
	endTime: timesChanged ? window.endTime : existing.endTime,
	...(newJob === null ? {} : { jobId: newJob.id, jobName: newJob.displayName }),
});

const moveProperties = (
	existing: NurseInShift,
	move: { location: string; window: ShiftWindow; section: Params["section"] },
	departmentId: string,
	timesChanged: boolean,
) => ({
	department_id: departmentId,
	nurse_id: existing.nurseId,
	shift_id: existing.id,
	from_location: existing.location,
	to_location: move.location,
	times_changed: timesChanged,
	section_name: move.section.name,
	section_location_key: move.section.locationKey,
	start_time: move.window.startTime.toFormat(QUICK_ADD_ANALYTICS_TIME_FORMAT),
	end_time: move.window.endTime.toFormat(QUICK_ADD_ANALYTICS_TIME_FORMAT),
});

const successMessage = (t: TFunction, name: string, fromLocation: string, toLocation: string) =>
	t("quick.add.move.success", { name, fromLocation, toLocation });

/**
 * The "+" dialog's move: the person's existing shift is updated in place (location, and times / job when the user
 * changed them) rather than a second shift being inserted — the insert is what the server refuses as an overlap.
 * Closes only once the update saved; a failed write has already shown its error snackbar and is tracked here.
 * `performMove` takes the shift to move as it stood when Save was clicked, not whatever the schedule says now.
 */
export const useQuickAddMove = (params: Params) => {
	const { t } = useTranslation();
	const formatPersonName = useFormatPersonName();
	const { track, trackError } = useAnalytics();
	const { showSnackbar } = useSnackbar();
	const { chosenDepartment } = useHospitalData();
	const jobManagementEnabled = useFeatureFlag(FEATURE_FLAGS.JobManagementEnabled);
	const { updateLocationAndHours, updateNurseLocationOrTimesLoading } = useUpdateLocationAndHoursForShift();
	const { nurse, location, window, job, jobTouched, section, onClose } = params;

	const performMove = useCallback(
		async (existing: NurseInShift): Promise<void> => {
			// The primary is disabled in every one of these states, so reaching here means a wiring bug, not a user action.
			if (!nurse || location === null) throw new Error("useQuickAddMove: move reached without a person or a destination");
			const timesChanged = !isSameShiftWindow(existing, window);
			// Read before the save: a successful move rewrites the schedule's shift object in place.
			const fromLocation = shiftLocationLabel(existing, t);
			const properties = moveProperties(existing, { location, window, section }, chosenDepartment.department_id, timesChanged);
			const newJob = jobForMove({ jobManagementEnabled, jobTouched, job });
			track("quick_add_nurse_move_start", properties);
			const moved = await updateLocationAndHours({
				originalNurseInShift: existing,
				updatedNurseInShift: movedShift(existing, location, window, newJob, timesChanged),
			});
			if (!moved) {
				trackError("quick_add_nurse_move_error", properties);
				return;
			}
			track("quick_add_nurse_move_success", properties);
			showSnackbar(successMessage(t, formatPersonName(nurse), fromLocation, location), "success");
			onClose();
		},
		[
			nurse,
			location,
			window,
			t,
			formatPersonName,
			section,
			chosenDepartment.department_id,
			jobManagementEnabled,
			jobTouched,
			job,
			updateLocationAndHours,
			trackError,
			track,
			showSnackbar,
			onClose,
		],
	);

	return { performMove, isMoving: updateNurseLocationOrTimesLoading };
};
